// src/pages/admin/AdminDashboard.js
import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  collection, doc, setDoc, deleteDoc,
  getDocs, orderBy, query, serverTimestamp, limit,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { ShieldCheck, Users, Ticket, Trash2, Plus, RefreshCw } from "lucide-react";

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function normalizeEmail(email) {
  return email?.toLowerCase().trim() ?? "";
}

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate?.() ?? new Date(ts);
  return d.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" });
}

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="card admin-stat-card">
      <div className="admin-stat-icon" style={{ color: accent }}>
        <Icon size={22} />
      </div>
      <div className="admin-stat-value" style={{ color: accent }}>{value}</div>
      <div className="admin-stat-label body-sm text-muted">{label}</div>
    </div>
  );
}

function SectionHeading({ children }) {
  return <h2 className="admin-section-heading">{children}</h2>;
}

// ─── WHITELIST PANEL ──────────────────────────────────────────────────────────
function WhitelistPanel({ currentUser }) {
  const [entries,   setEntries]   = useState([]);
  const [newEmail,  setNewEmail]  = useState("");
  const [newLabel,  setNewLabel]  = useState("");
  const [loading,   setLoading]   = useState(true);
  const [adding,    setAdding]    = useState(false);
  const [feedback,  setFeedback]  = useState(null); // { type: "success"|"error", msg }

  const showFeedback = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 3500);
  };

  const fetchWhitelist = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "whitelist"));
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => a.id.localeCompare(b.id));
      setEntries(rows);
    } catch {
      showFeedback("error", "Failed to load whitelist.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchWhitelist(); }, [fetchWhitelist]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const email = normalizeEmail(newEmail);
    if (!email) return;

    // Basic format check — full validation happens in AuthContext
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showFeedback("error", "Please enter a valid email address.");
      return;
    }

    if (entries.find(en => en.id === email)) {
      showFeedback("error", `${email} is already whitelisted.`);
      return;
    }

    setAdding(true);
    try {
      await setDoc(doc(db, "whitelist", email), {
        approved:  true,
        label:     newLabel.trim() || email,
        addedAt:   serverTimestamp(),
        addedBy:   currentUser.uid,
      });
      setNewEmail("");
      setNewLabel("");
      showFeedback("success", `${email} added to whitelist.`);
      await fetchWhitelist();
    } catch {
      showFeedback("error", "Failed to add email. Check Firestore rules.");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Remove ${email} from the whitelist? They will no longer be able to sign in.`)) return;
    try {
      await deleteDoc(doc(db, "whitelist", email));
      showFeedback("success", `${email} removed.`);
      await fetchWhitelist();
    } catch {
      showFeedback("error", "Failed to remove. Check Firestore rules.");
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-panel-header">
        <SectionHeading>Whitelist Management</SectionHeading>
        <button
          onClick={fetchWhitelist}
          className="btn btn-secondary btn-sm"
          aria-label="Refresh whitelist"
        >
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      <p className="body-sm text-muted mb-16">
        Only emails listed here can sign in or register. Document ID = email (lowercase).
        Firestore path: <code className="admin-code">/whitelist/{"{email}"}</code>
      </p>

      {/* Add form */}
      <form onSubmit={handleAdd} className="admin-whitelist-form">
        <div className="form-group" style={{ flex: 2, margin: 0 }}>
          <label className="form-label" htmlFor="wl-email">Email address *</label>
          <input
            id="wl-email"
            type="email"
            className="form-input"
            placeholder="client@example.com"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            required
            autoComplete="off"
          />
        </div>
        <div className="form-group" style={{ flex: 1, margin: 0 }}>
          <label className="form-label" htmlFor="wl-label">Label (optional)</label>
          <input
            id="wl-label"
            type="text"
            className="form-input"
            placeholder="Client name"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
          />
        </div>
        <div className="admin-whitelist-form-action">
          <label className="form-label" style={{ visibility: "hidden" }}>Add</label>
          <button type="submit" className="btn btn-primary" disabled={adding}>
            <Plus size={16} />
            {adding ? "Adding…" : "Add to Whitelist"}
          </button>
        </div>
      </form>

      {feedback && (
        <div className={`alert ${feedback.type === "success" ? "alert-success" : "alert-error"} mb-16`}>
          {feedback.msg}
        </div>
      )}

      {/* Entries list */}
      {loading ? (
        <div className="admin-loading"><div className="spinner" /></div>
      ) : entries.length === 0 ? (
        <div className="admin-empty">
          <ShieldCheck size={32} />
          <p>No whitelisted emails yet. Add one above.</p>
        </div>
      ) : (
        <div className="admin-list">
          {/* Header row */}
          <div className="admin-list-header">
            <span>Email</span>
            <span>Label</span>
            <span>Added</span>
            <span>Status</span>
            <span />
          </div>
          {entries.map((entry, i) => (
            <motion.div
              key={entry.id}
              className="admin-list-row"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
            >
              <span className="admin-list-email">{entry.id}</span>
              <span className="body-sm text-muted">{entry.label || "—"}</span>
              <span className="body-sm text-muted">{formatDate(entry.addedAt)}</span>
              <span>
                <span className={`badge ${entry.approved ? "badge-accent" : "badge-red"}`}>
                  {entry.approved ? "Approved" : "Revoked"}
                </span>
              </span>
              <button
                onClick={() => handleRemove(entry.id)}
                className="admin-remove-btn"
                aria-label={`Remove ${entry.id}`}
                title="Remove from whitelist"
              >
                <Trash2 size={15} />
              </button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── UNIFIED INQUIRIES & TICKETS PANEL ──────────────────────────────────────────
function TicketsPanel() {
  const [dataList, setDataList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("inquiries"); // "inquiries" | "tickets"
  const [selectedItem, setSelectedItem] = useState(null);

  const STATUS_MAP = {
    new:         { label: "New",         cls: "badge-amber" },
    open:        { label: "Open",        cls: "badge-amber" },
    "in-progress":{ label: "In Progress", cls: "badge-blue"  },
    resolved:    { label: "Resolved",    cls: "badge-accent" },
  };

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const snap = await getDocs(
          query(collection(db, viewMode), orderBy("createdAt", "desc"), limit(30))
        );
        setDataList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(`Error loading ${viewMode}:`, err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [viewMode]);

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      await setDoc(doc(db, viewMode, id), { status: newStatus }, { merge: true });
      setDataList(prev => prev.map(item => item.id === id ? { ...item, status: newStatus } : item));
      setSelectedItem(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  return (
    <div className="admin-panel">
      <div className="flex justify-between items-center mb-6">
        <SectionHeading>Communications</SectionHeading>
        
        {/* Toggle Switch */}
        <div style={{ display: "flex", background: "var(--surface-hover)", borderRadius: "8px", padding: "4px", flexWrap: "wrap" }}>
            <button 
              onClick={() => setViewMode("inquiries")}
              className={`admin-toggle-btn ${viewMode === "inquiries" ? "active" : ""}`}
              style={{ padding: "6px 12px", fontSize: "0.8125rem", fontWeight: 600, border: "none", borderRadius: "6px", cursor: "pointer", background: viewMode === "inquiries" ? "var(--primary)" : "transparent", color: viewMode === "inquiries" ? "var(--navy)" : "var(--text-muted)", transition: "all 0.2s" }}
            >
              Public Quotes
            </button>
            <button 
              onClick={() => setViewMode("tickets")}
              style={{ padding: "6px 12px", fontSize: "0.8125rem", fontWeight: 600, border: "none", borderRadius: "6px", cursor: "pointer", background: viewMode === "tickets" ? "var(--primary)" : "transparent", color: viewMode === "tickets" ? "var(--navy)" : "var(--text-muted)", transition: "all 0.2s" }}
            >
              Client Support
            </button>
            <button 
              onClick={() => setViewMode("upgrade_requests")}
              style={{ padding: "6px 12px", fontSize: "0.8125rem", fontWeight: 600, border: "none", borderRadius: "6px", cursor: "pointer", background: viewMode === "upgrade_requests" ? "var(--primary)" : "transparent", color: viewMode === "upgrade_requests" ? "var(--navy)" : "var(--text-muted)", transition: "all 0.2s" }}
            >
              Upgrades
            </button>
          </div>
        </div>

        {loading ? (
          <div className="admin-loading"><div className="spinner" /></div>
        ) : dataList.length === 0 ? (
          <div className="admin-empty">
            <Ticket size={32} />
            <p>No {viewMode.replace("_", " ")} found.</p>
          </div>
        ) : (
          <div className="admin-list">
            <div className="admin-list-header admin-list-header--tickets">
              <span>{viewMode === "inquiries" ? "Name" : viewMode === "upgrade_requests" ? "Client ID" : "Subject"}</span>
              <span>Email</span>
              <span>{viewMode === "inquiries" ? "Subject" : viewMode === "upgrade_requests" ? "Requested Upgrade" : "Category"}</span>
              <span>Date</span>
              <span>Status</span>
            </div>
            {dataList.map((item, i) => (
              <motion.div
                key={item.id}
                onClick={() => setSelectedItem(item)}
                className="admin-list-row admin-list-row--tickets"
                style={{ cursor: "pointer", transition: "background 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = "var(--surface-hover)"}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = "transparent"}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.025 }}
              >
                <span className="admin-ticket-subject" style={{ fontWeight: 600 }}>
                  {viewMode === "inquiries" ? item.name : viewMode === "upgrade_requests" ? (item.email?.split('@')[0] || "Client") : item.subject}
                </span>
                <span className="body-sm text-muted">{item.email || "—"}</span>
                <span className="body-sm text-muted" style={{ textTransform: "capitalize" }}>
                  {viewMode === "inquiries" ? (item.subject || "General") : viewMode === "upgrade_requests" ? item.upgradeName : (item.category || "General")}
                </span>
                <span className="body-sm text-muted">{formatDate(item.createdAt)}</span>
                <span>
                  <span className={`badge ${STATUS_MAP[item.status]?.cls || "badge-amber"}`}>
                    {STATUS_MAP[item.status]?.label || "New"}
                  </span>
                </span>
              </motion.div>
            ))}
          </div>
        )}

      {/* ─── DETAIL MODAL ────────────────────────────────────────── */}
      {selectedItem && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem"
        }}>
          <div style={{
            backgroundColor: "var(--surface)", border: "1px solid var(--border-med)",
            borderRadius: "1rem", width: "100%", maxWidth: "640px", padding: "2rem",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)", position: "relative"
          }}>
            <button
              onClick={() => setSelectedItem(null)}
              style={{ position: "absolute", top: "1.5rem", right: "1.5rem", background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: "1.25rem" }}
            >
              ✕
            </button>

            <div style={{ marginBottom: "1.5rem" }}>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", marginBottom: "0.75rem" }}>
                <span className={`badge ${STATUS_MAP[selectedItem.status]?.cls || "badge-amber"}`}>
                  {STATUS_MAP[selectedItem.status]?.label || "Open"}
                </span>
                <span style={{ fontSize: "0.875rem", color: "var(--text-muted)" }}>
                  {formatDate(selectedItem.createdAt)}
                </span>
                {viewMode === "tickets" && selectedItem.priority && (
                  <span style={{ fontSize: "0.75rem", padding: "2px 8px", borderRadius: "10px", border: "1px solid var(--border)" }}>
                    {selectedItem.priority} priority
                  </span>
                )}
              </div>
              <h2 style={{ fontSize: "1.5rem", fontWeight: 700, marginBottom: "0.25rem", color: "var(--text)" }}>
                {selectedItem.subject}
              </h2>
              <div style={{ fontSize: "0.875rem", color: "var(--text-muted)" }}>
                <p>From: <strong style={{ color: "var(--text)" }}>{selectedItem.name || selectedItem.email}</strong></p>
                {selectedItem.phone && <p>Phone: {selectedItem.phone}</p>}
              </div>
            </div>

            <div style={{
              backgroundColor: "var(--background)", border: "1px solid var(--border)",
              borderRadius: "0.5rem", padding: "1.25rem", minHeight: "150px", maxHeight: "300px", overflowY: "auto",
              whiteSpace: "pre-wrap", fontSize: "0.9375rem", lineHeight: 1.6, marginBottom: "2rem", color: "var(--text)"
            }}>
              {selectedItem.message}
            </div>

            <div style={{ display: "flex", gap: "1rem", justifyContent: "space-between", flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                {selectedItem.status !== "in-progress" && (
                  <button className="btn btn-secondary btn-sm" onClick={() => handleUpdateStatus(selectedItem.id, "in-progress")}>
                    Mark In-Progress
                  </button>
                )}
                {selectedItem.status !== "resolved" && (
                  <button className="btn btn-secondary btn-sm" onClick={() => handleUpdateStatus(selectedItem.id, "resolved")}>
                    Mark Resolved
                  </button>
                )}
              </div>
              <a 
                href={`mailto:${selectedItem.email}?subject=Re: ${selectedItem.subject}`} 
                className="btn btn-primary"
              >
                Reply via Email
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const { currentUser, userProfile } = useAuth();
  const [userCount,   setUserCount]   = useState("—");
  const [wlCount,     setWlCount]     = useState("—");
  const [ticketCount, setTicketCount] = useState("—");

  useEffect(() => {
    async function loadStats() {
      try {
        const [userSnap, wlSnap, tkSnap] = await Promise.all([
          getDocs(collection(db, "users")),
          getDocs(collection(db, "whitelist")),
          getDocs(query(collection(db, "inquiries"), orderBy("createdAt", "desc"), limit(100))),
        ]);
        setUserCount(userSnap.size);
        setWlCount(wlSnap.size);
        setTicketCount(tkSnap.docs.filter(d => d.data().status !== "resolved").length);
      } catch {
        // Stats are non-critical
      }
    }
    loadStats();
  }, []);

  return (
    <div className="admin-dashboard">

      {/* ── Page header ── */}
      <motion.div
        className="admin-dashboard-header"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div>
          <div className="section-label">Admin Panel</div>
          <h1 className="heading-1">Control Center</h1>
          <p className="body-sm text-muted mt-8">
            Signed in as <strong>{userProfile?.email || currentUser?.email}</strong>
          </p>
        </div>
        <span className="badge badge-red">
          <ShieldCheck size={12} />
          Admin Access
        </span>
      </motion.div>

      {/* ── Stat strip ── */}
      <motion.div
        className="admin-stats-strip"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08 }}
      >
        <StatCard icon={Users}        label="Registered Users"   value={userCount}   accent="var(--accent)" />
        <StatCard icon={ShieldCheck}  label="Whitelisted Emails" value={wlCount}     accent="#60a5fa" />
        <StatCard icon={Ticket}       label="Open Tickets"       value={ticketCount} accent="var(--accent-warm)" />
      </motion.div>

      {/* ── Asymmetric main grid ── */}
      {/* Left column (wider): whitelist. Right column (narrower): tickets. */}
      <div className="admin-main-grid">

        {/* Whitelist — wider column */}
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.14 }}
        >
          <WhitelistPanel currentUser={currentUser} />
        </motion.div>

        {/* Tickets — narrower column */}
        <motion.div
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.18 }}
        >
          <TicketsPanel />
        </motion.div>

      </div>
    </div>
  );
}
